import Table, { Order } from 'components/Table/Table';
import { useGetDashboardReports } from 'hooks/CodegenDashboardHooks';

import { DefaultRoutes } from 'Routes';
import ReportsTile from 'components/ReportsTile/ReportsTile';
import { Typography } from '@trackman/web-shared-components';
import { displayDate } from 'utilities/dateHelpers';
import { useNavigate } from 'react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportItemFilterDto, ReportSubType, ReportType } from 'types/CodegenDashboardTypes';
import { GrouppedChip } from 'components/SearchChips/SearchChips';
import { useApplicationUserRightsStore } from 'stores/ApplicationUserRightsStore';
import { AppPermissions } from 'constants/AppPermissions';

const reportTypeMap: Record<string, ReportType> = {
  [AppPermissions.PlayerReports]: 'PlayerReport',
  [AppPermissions.SessionReports]: 'SessionReviewReport',
  [AppPermissions.UmpireReports]: 'UmpireReport',
};

const reportSubTypeMap: Record<string, ReportSubType[]> = {
  [AppPermissions.PlayerReports]: ['Batter', 'Pitcher'],
  [AppPermissions.SessionReports]: ['BatterSingleSession', 'BatterAggregated', 'PitcherSingleSession', 'PitcherAggregated'],
  [AppPermissions.UmpireReports]: ['SingleSession', 'AllSessions'],
};

const reportTypeOrder = [AppPermissions.PlayerReports, AppPermissions.SessionReports, AppPermissions.UmpireReports];

const Homepage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: recentReports, isLoading: isLoadingRecentReports } = useGetDashboardReports();
  const { permissions, isAdmin } = useApplicationUserRightsStore();

  const availableReportTypes: { reportType: ReportType; reportSubTypes: ReportSubType[] }[] = useMemo(() => {
    const items = permissions
      .filter((permission) => reportTypeOrder.includes(permission))
      .sort((a, b) => reportTypeOrder.indexOf(a) - reportTypeOrder.indexOf(b))
      .map((permission) => ({
        reportType: reportTypeMap[permission],
        reportSubTypes: [...reportSubTypeMap[permission]],
      }));

    if (isAdmin) {
      items.find((item) => item.reportType === 'UmpireReport')?.reportSubTypes.push('AdminOverview');
    }

    return items;
  }, [permissions, isAdmin]);

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof Exclude<typeof recentReports, undefined>[number] | ''>('generatedAt');

  return (
    <div className='container flex-column pd-4 gap-4'>
      <div className='flex-row gap-2'>
        {availableReportTypes?.map((report) => <ReportsTile key={report.reportType} report={report} />) ??
          [0, 1].map((skeleton) => <ReportsTile key={skeleton} />)}
      </div>

      <div className='flex-column gap-1'>
        <Typography variant='h2'>{t('Last generated reports')}</Typography>
        <Table
          rows={recentReports ?? []}
          pageState={[page, setPage]}
          orderState={[order, setOrder]}
          orderByState={[orderBy, setOrderBy]}
          columns={[
            {
              field: 'reportType',
              headerName: t('Report'),
              renderCell: (_, row) => t(`${row.reportType}.${row.reportSubType}`),
              width: '25%',
            },
            {
              field: 'reportFilter',
              headerName: t('Time period'),
              renderCell: (filter: ReportItemFilterDto) =>
                filter.seasonNames?.length ? (
                  <GrouppedChip groupName={t('chip.season')} label={filter.seasonNames} />
                ) : (
                  filter.dateRanges && (
                    <>
                      <GrouppedChip groupName={t('chip.from')} label={displayDate(filter.dateRanges[0].utcDateFrom)} />
                      <GrouppedChip groupName={t('chip.to')} label={displayDate(filter.dateRanges[0].utcDateTo)} />
                    </>
                  )
                ),
              width: '15%',
            },
            {
              field: 'reportFilter',
              headerName: `${t('Leagues')} / ${t('Level')}`,
              renderCell: (filter: ReportItemFilterDto) =>
                filter.level?.name ? (
                  <GrouppedChip label={filter.level.name} />
                ) : filter.leagues?.length ? (
                  <GrouppedChip groupName={t('chip.leagueNames')} label={filter.leagues.map((x) => x.name)} />
                ) : (
                  <></>
                ),
              width: '15%',
            },
            {
              field: 'reportFilter',
              headerName: t('Team'),
              renderCell: (filter: ReportItemFilterDto) => filter.team?.name && <GrouppedChip label={filter.team?.name} />,
              width: '15%',
            },
            {
              field: 'reportFilter',
              headerName: t('Participants'),
              renderCell: (filter: ReportItemFilterDto) =>
                filter.players.length ? (
                  <GrouppedChip groupName={t('chip.playerNames')} label={filter.players.map((x) => x.name)} />
                ) : filter.umpires.length ? (
                  <GrouppedChip groupName={t('chip.umpireNames')} label={filter.umpires.map((x) => x.name)} />
                ) : (
                  <></>
                ),
              width: '15%',
            },
            {
              field: 'generatedAt',
              headerName: t('generatedAt'),
              renderCell: (cellValue) => displayDate(cellValue),
              width: '15%',
            },
          ]}
          loading={isLoadingRecentReports}
          noRowsLabel={t('No reports found.')}
          onRowClick={(row) => {
            const reportRoute = DefaultRoutes[row.reportType][row.reportSubType];
            if (reportRoute) navigate(`${reportRoute}/${row.reportId}`, { viewTransition: true });
          }}
        />
      </div>
    </div>
  );
};

export default Homepage;
